import React from 'react';

/**
 * Minimal, dependency-free Markdown renderer for assistant chat messages.
 *
 * SPEC-114: planner answers arrive as GitHub-flavoured markdown (bold, lists,
 * tables, code spans, headings, rules). Rather than pull in marked/markdown-it +
 * DOMPurify, we render the small subset we actually emit straight to React
 * elements. Because we NEVER use dangerouslySetInnerHTML, React escapes every
 * text node for us — assistant text is untrusted LLM output, so this is the
 * safest possible path (no HTML injection surface at all). Links are the one
 * attribute sink, so their href scheme is whitelisted below.
 */

// ---- inline parsing --------------------------------------------------------

// No lookbehind/lookahead here — a regex literal with syntax an older engine
// (e.g. Safari < 16.4) can't parse throws at MODULE-EVAL time and would take the
// whole chat chunk down rather than degrade. Underscore word-boundary handling
// (so snake_case identifiers like fs_forecast_weekly aren't italicised) is done
// in plain JS in the group-5 branch below.
const INLINE_RE =
  /(`[^`]+`)|(\*\*[^*]+?\*\*|__[^_]+?__)|(~~[^~]+?~~)|(\[[^\]]+?\]\([^)\s]+?\))|(\*[^*\s][^*]*?\*|_[^_\s][^_]*?_)/;

// Protocol-relative //host resolves to https://host, so require the path branch
// to be a single leading slash (not //). Blocks javascript:, data:, vbscript:, etc.
const SAFE_HREF_RE = /^(https?:\/\/|mailto:|\/(?!\/)|#)/i;
const WORD_CHAR_RE = /[A-Za-z0-9_]/;

/** Render a run of inline markdown (bold, italic, code, strike, links) to nodes. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let pos = 0; // absolute index into `text`
  let i = 0;
  while (pos < text.length) {
    const m = INLINE_RE.exec(text.slice(pos));
    if (!m || m.index === undefined) {
      nodes.push(text.slice(pos));
      break;
    }
    const matchStart = pos + m.index;
    const token = m[0];

    // Underscore emphasis is only valid at word boundaries — reject e.g.
    // fs_forecast_weekly. Emit the stray "_" as text and advance past it so the
    // remainder still gets parsed. (Asterisk emphasis has no intraword rule.)
    if (m[5] && token[0] === '_') {
      const before = text[matchStart - 1];
      const after = text[matchStart + token.length];
      if ((before && WORD_CHAR_RE.test(before)) || (after && WORD_CHAR_RE.test(after))) {
        if (m.index > 0) nodes.push(text.slice(pos, matchStart));
        nodes.push('_');
        pos = matchStart + 1;
        continue;
      }
    }

    if (m.index > 0) {
      nodes.push(text.slice(pos, matchStart));
    }
    const key = `${keyPrefix}-i${i++}`;
    if (m[1]) {
      // `code`
      nodes.push(
        <code
          key={key}
          className="px-1 py-0.5 rounded bg-black/30 font-mono text-[0.85em] text-[var(--text-primary)]"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (m[2]) {
      // **bold** or __bold__
      nodes.push(
        <strong key={key} className="font-semibold text-[var(--text-primary)]">
          {renderInline(token.slice(2, -2), key)}
        </strong>,
      );
    } else if (m[3]) {
      // ~~strike~~
      nodes.push(
        <del key={key} className="opacity-70">
          {renderInline(token.slice(2, -2), key)}
        </del>,
      );
    } else if (m[4]) {
      // [text](href)
      const split = token.indexOf('](');
      const label = token.slice(1, split);
      const href = token.slice(split + 2, -1);
      if (SAFE_HREF_RE.test(href)) {
        nodes.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400 underline underline-offset-2 hover:text-amber-300"
          >
            {renderInline(label, key)}
          </a>,
        );
      } else {
        // Unsafe scheme (javascript:, data:, etc.) — render as plain text.
        nodes.push(<span key={key}>{label}</span>);
      }
    } else if (m[5]) {
      // *italic* or _italic_
      nodes.push(
        <em key={key} className="italic">
          {renderInline(token.slice(1, -1), key)}
        </em>,
      );
    }
    pos = matchStart + token.length;
  }
  return nodes;
}

// ---- block parsing ---------------------------------------------------------

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const HR_RE = /^\s*([-*_])\1{2,}\s*$/;
const UL_RE = /^(\s*)[-*+]\s+(.*)$/;
const OL_RE = /^(\s*)(\d+)[.)]\s+(.*)$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/;

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function alignFromSep(cell: string): 'left' | 'center' | 'right' {
  const c = cell.trim();
  const l = c.startsWith(':');
  const r = c.endsWith(':');
  if (l && r) return 'center';
  if (r) return 'right';
  return 'left';
}

interface Block {
  render(key: string): React.ReactNode;
}

/** Parse markdown source into a flat list of block renderers. */
function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // blank line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // fenced code block
    const fence = line.match(/^\s*```(.*)$/);
    if (fence) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // consume closing fence
      const code = codeLines.join('\n');
      blocks.push({
        render: (key) => (
          <pre
            key={key}
            className="my-2 overflow-x-auto rounded-lg bg-black/30 p-3 text-xs font-mono text-[var(--text-secondary)] leading-relaxed"
          >
            <code>{code}</code>
          </pre>
        ),
      });
      continue;
    }

    // horizontal rule
    if (HR_RE.test(line)) {
      blocks.push({
        render: (key) => (
          <hr key={key} className="my-3 border-0 border-t border-[var(--border-subtle)]" />
        ),
      });
      i++;
      continue;
    }

    // heading
    const h = line.match(HEADING_RE);
    if (h) {
      const level = h[1].length;
      const content = h[2];
      const sizes = ['text-base', 'text-base', 'text-sm', 'text-sm', 'text-xs', 'text-xs'];
      blocks.push({
        render: (key) => (
          <div
            key={key}
            className={`${sizes[level - 1]} font-semibold text-[var(--text-primary)] mt-3 mb-1.5 first:mt-0`}
          >
            {renderInline(content, key)}
          </div>
        ),
      });
      i++;
      continue;
    }

    // table (header row followed by a separator row)
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1])) {
      const header = splitTableRow(line);
      const aligns = splitTableRow(lines[i + 1]).map(alignFromSep);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      const alignClass = (n: number) =>
        aligns[n] === 'right' ? 'text-right' : aligns[n] === 'center' ? 'text-center' : 'text-left';
      blocks.push({
        render: (key) => (
          <div key={key} className="my-2 overflow-x-auto rounded-lg border border-[var(--border-subtle)]">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[var(--bg-overlay)]">
                  {header.map((cell, n) => (
                    <th
                      key={n}
                      className={`px-2.5 py-1.5 font-semibold text-[var(--text-primary)] border-b border-[var(--border-subtle)] whitespace-nowrap ${alignClass(n)}`}
                    >
                      {renderInline(cell, `${key}-h${n}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, r) => (
                  <tr key={r} className="border-b border-[var(--border-subtle)] last:border-0">
                    {row.map((cell, n) => (
                      <td
                        key={n}
                        className={`px-2.5 py-1.5 text-[var(--text-secondary)] align-top ${alignClass(n)}`}
                      >
                        {renderInline(cell, `${key}-r${r}c${n}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
      });
      continue;
    }

    // list (unordered or ordered) — supports one level of nesting via indent
    if (UL_RE.test(line) || OL_RE.test(line)) {
      const ordered = OL_RE.test(line) && !UL_RE.test(line);
      const items: { indent: number; content: string }[] = [];
      while (i < lines.length) {
        const ul = lines[i].match(UL_RE);
        const ol = lines[i].match(OL_RE);
        if (ul) {
          items.push({ indent: ul[1].length, content: ul[2] });
          i++;
        } else if (ol) {
          items.push({ indent: ol[1].length, content: ol[3] });
          i++;
        } else if (lines[i].trim() !== '' && /^\s+\S/.test(lines[i]) && items.length > 0) {
          // continuation line for the previous item
          items[items.length - 1].content += ' ' + lines[i].trim();
          i++;
        } else {
          break;
        }
      }
      const baseIndent = Math.min(...items.map((it) => it.indent));
      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push({
        render: (key) => (
          <ListTag
            key={key}
            className={`my-2 space-y-1 ${ordered ? 'list-decimal' : 'list-disc'} pl-5 marker:text-[var(--text-tertiary)]`}
          >
            {items.map((it, n) => (
              <li
                key={n}
                className="text-[var(--text-secondary)] leading-relaxed"
                style={it.indent > baseIndent ? { marginLeft: '1rem' } : undefined}
              >
                {renderInline(it.content, `${key}-li${n}`)}
              </li>
            ))}
          </ListTag>
        ),
      });
      continue;
    }

    // blockquote
    if (/^\s*>\s?/.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      const content = quoteLines.join(' ');
      blocks.push({
        render: (key) => (
          <blockquote
            key={key}
            className="my-2 border-l-2 border-[var(--border-default)] pl-3 text-[var(--text-secondary)] italic"
          >
            {renderInline(content, key)}
          </blockquote>
        ),
      });
      continue;
    }

    // paragraph — gather consecutive non-blank, non-special lines
    const paraLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !HEADING_RE.test(lines[i]) &&
      !HR_RE.test(lines[i]) &&
      !UL_RE.test(lines[i]) &&
      !OL_RE.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i]) &&
      !/^\s*```/.test(lines[i]) &&
      !(lines[i].includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]))
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    const para = paraLines.join('\n');
    blocks.push({
      render: (key) => (
        <p key={key} className="my-1.5 leading-relaxed first:mt-0 last:mb-0">
          {para.split('\n').map((ln, n, arr) => (
            <React.Fragment key={n}>
              {renderInline(ln, `${key}-l${n}`)}
              {n < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      ),
    });
  }

  return blocks;
}

interface MarkdownMessageProps {
  content: string;
  className?: string;
}

/** Render assistant markdown content as sanitized React elements. */
export function MarkdownMessage({ content, className }: MarkdownMessageProps) {
  const blocks = React.useMemo(() => parseBlocks(content), [content]);
  return (
    <div className={`text-sm text-[var(--text-secondary)] ${className ?? ''}`}>
      {blocks.map((b, n) => b.render(`b${n}`))}
    </div>
  );
}
